// src/scripts/PhotoUpload/index.js
import React, { useState, useRef, useCallback } from "react";
import { useImageProcessor } from "./hooks/useImageProcessor";
import { usePhotoUpload } from "./hooks/usePhotoUpload";
import FilePreview from "./components/FilePreview";
import { generateFileId } from "./utils/formatters";

const PhotoUpload = ({
  parkId,
  onUploadSuccess = () => {},
  onUploadError = () => {},
  maxFiles = 5,
}) => {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const { processImage } = useImageProcessor();
  const { uploadPhoto } = usePhotoUpload(parkId);

  const handleFileSelect = useCallback(
    async (event) => {
      const selectedFiles = Array.from(event.target.files);

      if (files.length + selectedFiles.length > maxFiles) {
        alert(`Maximum ${maxFiles} photos allowed`);
        return;
      }

      setIsProcessing(true);

      for (const file of selectedFiles) {
        const fileId = generateFileId();

        // Add file to state immediately
        setFiles((prev) => [
          ...prev,
          {
            id: fileId,
            name: file.name,
            status: "processing",
            progress: 0,
          },
        ]);

        try {
          // Process the image
          const processedImage = await processImage(file, (progressData) => {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === fileId
                  ? {
                      ...f,
                      progress: progressData.progress * 100,
                      status: progressData.stage,
                    }
                  : f
              )
            );
          });

          // Create preview URL and update state
          const previewUrl = URL.createObjectURL(processedImage.compressedFile);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? {
                    ...f,
                    processedImage,
                    previewUrl,
                    status: "ready",
                    progress: 100,
                  }
                : f
            )
          );
        } catch (error) {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? { ...f, status: "error", error: error.message }
                : f
            )
          );
        }
      }

      setIsProcessing(false);
      event.target.value = ""; // Clear input
    },
    [files.length, maxFiles, processImage]
  );

  const handleUpload = useCallback(
    async (fileData) => {
      if (!fileData.processedImage) return;

      try {
        const result = await uploadPhoto(
          fileData.processedImage,
          (progressData) => {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === fileData.id
                  ? {
                      ...f,
                      progress: progressData.progress * 100,
                      status: progressData.stage,
                    }
                  : f
              )
            );
          }
        );

        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? { ...f, status: "uploaded", uploadResult: result }
              : f
          )
        );

        onUploadSuccess(result);
      } catch (error) {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? { ...f, status: "error", error: error.message }
              : f
          )
        );
        onUploadError(error);
      }
    },
    [uploadPhoto, onUploadSuccess, onUploadError]
  );

  const removeFile = useCallback((fileId) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);
      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  }, []);

  return (
    <div>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-6">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={handleFileSelect}
          disabled={isProcessing || files.length >= maxFiles}
          className="hidden"
        />

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing || files.length >= maxFiles}
          className="bg-earth-700 text-white px-6 py-3 rounded-lg hover:bg-earth-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isProcessing ? "Processing Images..." : "Select Photos"}
        </button>

        <p className="text-gray-600 mt-2">
          {files.length}/{maxFiles} photos • Max 20MB per photo
        </p>
      </div>

      {files.length > 0 && (
        <div className="space-y-4">
          {files.map((file) => (
            <FilePreview
              key={file.id}
              file={file}
              onUpload={() => handleUpload(file)}
              onRemove={() => removeFile(file.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
